import { useContext, useState, useEffect, useCallback, useMemo, ChangeEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
    Button,
    FormGroup,
    FormLabel,
    MenuItem,
    Radio,
    SelectChangeEvent,
} from '@mui/material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid'
import { Text } from '@hubdevs/viu-ui-shared';
import { ApiServiceContext } from '../../api/ApiService.context';
import {
    ATTRIBUTION_TYPE_DEFAULT,
    JoinedOrganizationAttributionSummary,
    JoinedQuoteExperienceSummary,
    JsonSchemaType,
    OrganizationAttributionSummary,
} from '../../api/attribution/types';
import { AppFormRow } from '../../components/AppFormRow/AppFormRow';
import { CollapsibleSection } from '../../components/CollapsibleSection/CollapsibleSection.component';
import { AppTableWithButtons } from '../../components/TableWithButtons/TableWithButtons';
import { AppPage } from '../../components/Page/Page';
import { AppFormCell } from '../../components/AppFormCell/AppFormCell';
import { AppSelect } from '../../components/AppSelect/AppSelect.component';
import { ApiServiceEndpointResponse } from '../../api/api.types';
import { Bundle, BundleAttribution } from '../../api/bundle/types';
import { AppTextField } from '../../components/AppTextField/AppTextField.component';
import './Bundle.page.css';
import { OrganizationService } from '../../api/organization/OrganizationService.class';
import { BundleService } from '../../api/bundle/BundleService.class';
import { AttributionService } from '../../api/attribution/AttributionService.class';
import { ApiServiceCollection } from '../../api/ApiServiceCollection.class';



type LoadingState = 'NOT STARTED' | 'LOADING' | 'SUCCESS' | 'FAIL';

type AttributionLoadingState = 'LOADING' | 'NOT LOADING';

type AttributionState = JoinedOrganizationAttributionSummary & {
    loadingState: AttributionLoadingState;
}

type AttributionAssociationCommand = {
    organizationAttributionGuid: string;
    included: boolean;
    action: 'CREATE' | 'DELETE';
}

const NONE_ROW_GUID = '__none__';

export const BundlePage = () => {

    const [bundleOrgAttrPageSize, setBundleOrgAttrPageSize] = useState<number>(100);

    const navigate = useNavigate();
    const { organizationGuid, bundleGuid } = useParams();
    const apiServiceContext = useContext(ApiServiceContext);
    const {
        OrganizationService,
        BundleService,
        AttributionService,
    } = apiServiceContext.getApiServiceCollections() as {
        OrganizationService: OrganizationService,
        BundleService: BundleService,
        AttributionService: AttributionService,
        [key: string]: ApiServiceCollection
    };

    const [localBundleGuid, setLocalBundleGuid] = useState<string | undefined>(bundleGuid);
    const [bundleLoadingState, setBundleLoadingState] = useState<LoadingState>('NOT STARTED');
    const [bundleError, setBundleError] = useState<string>('');
    const [displayName, setDisplayName] = useState<string>('');

    const [attributions, setAttributions] = useState<OrganizationAttributionSummary[]>([]);
    const [attrTotal, setAttrTotal] = useState<number>(0);
    const [attrPage, setAttrPage] = useState<number>(0);
    const [attrPageSize, setAttrPageSize] = useState<number>(10);
    const [attrLoadingState, setAttrLoadingState] = useState<LoadingState>('NOT STARTED');
    const [attrError, setAttrError] = useState<string>('');

    const [bundleOrgAttributions, setBundleOrgAttributions] = useState<BundleAttribution[]>([]);
    const [bundleOrgAttrTotal, setBundleOrgAttrTotal] = useState<number>(1000);
    const [bundleOrgAttrPage, setBundleOrgAttrPage] = useState<number>(0);
    const [bundleOrgAttrLastPage, setBundleOrgAttrLastPage] = useState<number>(-1);
    const [bundleOrgAttributionsError, setBundleOrgAttributionsError] = useState<string>('');
    const [bundleOrgAttrLoadingState, setBundleOrgAttrLoadingState] = useState<LoadingState>('NOT STARTED');
    const [bundleFormReady, setBundleFormReady] = useState<boolean>(false);

    const [quoteExperiences, setQuoteExperiences] = useState<Bundle[]>();
    const [quoteExperiencesError, setQuoteExperiencesError] = useState<string>('');
    const [totalQuoteExperienceRecords, setTotalQuoteExperienceRecords] = useState(0);
    const [paginationModelQuoteExperiences, setPaginationModelQuoteExperiences] = useState({ page: 0, pageSize: 10 });
    const [quoteExperiencesLoadingState, setQuoteExperiencesLoadingState] = useState<LoadingState>('NOT STARTED');

    const [attributionDataProcessed, setAttributionDataProcessed] = useState<boolean>(false);
    const [joinedAttributions, setJoinedAttributions] = useState<JoinedOrganizationAttributionSummary[]>([]);
    const [attributionState, setAttributionState] = useState<Record<string, AttributionState>>({});
    const [commands, setCommands] = useState<AttributionAssociationCommand[]>([]);
    const [selectedQuoteExperienceGuid, setSelectedQuoteExperienceGuid] = useState<string | null>(null);
    const [quoteExperienceChanged, setQuoteExperienceChanged] = useState<boolean>(false);

    const [schemas, setSchemas] = useState<JsonSchemaType[]>([]);
    const [schemasError, setSchemasError] = useState<string>('');
    const [selectedType, setSelectedType] = useState<string | null>(ATTRIBUTION_TYPE_DEFAULT);
    const [isSaving, setIsSaving] = useState(false);
    const [commandProcessingState, setCommandProcessingState] = useState<AttributionLoadingState>('NOT LOADING');

    const latestSchemas = useMemo(() => {
        const reduced = schemas.reduce((acc, cur) => {
            if (!acc[cur.schema_type]) {
                acc[cur.schema_type] = cur;
                return acc;
            }
            if (cur.schema_type_version > acc[cur.schema_type].schema_type_version) {
                acc[cur.schema_type] = cur;
            }
            return acc;
        }, {} as Record<string, JsonSchemaType>);
        return Object.values(reduced);
    }, [schemas]);

    useEffect(() => {
        if (bundleOrgAttrLoadingState === 'SUCCESS') {
            setBundleFormReady(true);
        }
    }, [bundleOrgAttrLoadingState]);

    useEffect(() => {
        setSchemasError('');
        const fetchSchemas = async () => {
            let schemasResp = await AttributionService.getAttributionSchemasByType({ type: 'attribution' });
            while (schemasResp.retry) {
                // TODO - Show retry attempts - might need to set the retry in state and loop in useEffect
                schemasResp = await schemasResp.retry.catch(error => error);
            }
            if (schemasResp.error) {
                setSchemasError('Failed to load all attribution schemas');
                return;
            }
            setSchemas(schemasResp.jsonBody!['schemas']);
        };
        fetchSchemas();
    }, [AttributionService]);

    const bundleAttributionsMap = useMemo(() => {
        if (bundleOrgAttrLoadingState !== 'SUCCESS') {
            return {};
        }
        return bundleOrgAttributions.reduce((acc, cur) => {
            acc[cur.organizationAttributionGuid] = cur;
            return acc;
        }, {} as Record<string, BundleAttribution>)
    }, [bundleOrgAttributions, bundleOrgAttrLoadingState]);

    useEffect(() => {
        if (attributionDataProcessed) {
            return;
        }
        setAttributionDataProcessed(true);
        const localJoinedAttr = attributions.map(attr => {
            const orgAttr = bundleAttributionsMap[attr.organizationAttributionGuid];
            return {
                ...attr,
                organizationGuid: organizationGuid!,
                included: !!orgAttr
            }
        });
        setAttributionState({
            ...attributionState,
            ...localJoinedAttr.reduce((acc, cur) => {
                acc[cur.organizationAttributionGuid] = {
                    ...cur,
                    loadingState: 'NOT LOADING'
                }
                return acc;
            }, {} as Record<string, AttributionState>)
        });
        setJoinedAttributions(localJoinedAttr);
    }, [attributions, bundleAttributionsMap, organizationGuid, attributionState, attributionDataProcessed]);


    const loadBundle = useCallback(async () => {
        if (localBundleGuid && bundleLoadingState !== 'NOT STARTED') {
            return;
        }
        setBundleError('');
        setBundleLoadingState('LOADING');
        let bundleResp = await BundleService.getBundleById(localBundleGuid);
        while (bundleResp.retry) {
            bundleResp = await bundleResp.retry.catch(error => error);
        }
        if (bundleResp.error) {
            setBundleError('Failed to load the selected Bundle.');
            setBundleLoadingState('FAIL');
            return;
        }
        const bundle = bundleResp.jsonBody!;
        setDisplayName(bundle.displayName);
        setSelectedQuoteExperienceGuid(bundle.parentGuid ?? null);
        setQuoteExperienceChanged(false);

        setBundleLoadingState('SUCCESS');
    }, [localBundleGuid, bundleLoadingState, BundleService]);

    const loadBundleOrganizationAttributionsPage = useCallback(async () => {
        setBundleOrgAttributionsError('');
        if (!localBundleGuid) {
            setBundleOrgAttrLoadingState('SUCCESS');
            return;
        }
        if (bundleOrgAttrPage * bundleOrgAttrPageSize >= bundleOrgAttrTotal) {
            setBundleOrgAttrLoadingState('SUCCESS');
            return;
        }

        let bunOrgAttrsResp = await BundleService.getAttributions(localBundleGuid);
        while (bunOrgAttrsResp.retry) {
            bunOrgAttrsResp = await bunOrgAttrsResp.retry.catch(error => error);
        }
        if (bunOrgAttrsResp.error) {
            setBundleOrgAttributionsError('Failed to load organization attributions');
            setBundleOrgAttrLoadingState('FAIL');
            return;
        }

        const bundleOrgAttrs = bunOrgAttrsResp.jsonBody || []; // Is an array of BundleAttribution

        setBundleOrgAttributions(prev => ([
            ...prev,
            ...bundleOrgAttrs
        ]));

        // TODO - this endpoint isn't paginated. See if this causes any issues
        setBundleOrgAttrPageSize(bundleOrgAttrs.length);
        setBundleOrgAttrTotal(bundleOrgAttrs.length);
        setBundleOrgAttrPage(bundleOrgAttrPage + 1);
    }, [
        bundleOrgAttrPage, bundleOrgAttrTotal, BundleService, localBundleGuid, bundleOrgAttrPageSize
    ]);

    const loadAllAttributionsPage = useCallback(async ({ page, pageSize, type }: { page: number, pageSize: number, type: string | null }) => {
        if (!type) {
            return;
        }
        setAttrError('');
        setAttrLoadingState('LOADING');
        let orgAttrsResp = await OrganizationService.getOrganizationAttributions({
            organizationGuid,
            skip: page * pageSize,
            limit: pageSize,
            query: { filter: { type } }
        });
        while (orgAttrsResp.retry) {
            // TODO - track retries and display in the table. Store retry in state and call in a useEffect
            orgAttrsResp = await orgAttrsResp.retry.catch(error => error);
        }
        if (orgAttrsResp.error) {
            setAttrError('Failed to load all attributions');
            setAttrLoadingState('FAIL');
            return;
        }
        setAttrTotal(orgAttrsResp.jsonBody!.total);
        setAttributions(orgAttrsResp.jsonBody!.data);
        setAttrLoadingState('SUCCESS');
        setAttributionDataProcessed(false);
    }, [OrganizationService, organizationGuid]);

    const loadQuoteExperiences = useCallback(async (page: number, pageSize: number) => {
        setQuoteExperiencesError('');
        if (!organizationGuid) {
            setQuoteExperiencesLoadingState('SUCCESS');
            setQuoteExperiences([] as Bundle[]);
            return;
        }
        let bundlesResp = await BundleService.getBundles({
            organizationGuid,
            skip: page * pageSize,
            limit: pageSize,
            query: {
                filter: {
                    type: 'QUOTE_EXPERIENCE'
                }
            }
        });
        while (bundlesResp.retry) {
            // TODO - track retries and display in the table. Store retry in state and call in a useEffect
            bundlesResp = await bundlesResp.retry.catch(error => error);
        }
        if (bundlesResp.error) {
            setQuoteExperiencesError('Failed to load Quote Experiences')
            setQuoteExperiencesLoadingState('FAIL');
            return
        }
        setQuoteExperiences(bundlesResp.jsonBody!.data);
        setTotalQuoteExperienceRecords(bundlesResp.jsonBody!.total);
        setQuoteExperiencesLoadingState('SUCCESS');
    }, [organizationGuid, BundleService]);

    // ****************
    // * PAGE LOADING *
    // ****************

    useEffect(() => {
        // Load Bundle itself
        if (localBundleGuid && bundleLoadingState === 'NOT STARTED') {
            loadBundle();
        }
    }, [localBundleGuid, bundleLoadingState, loadBundle]);

    // Calling this method should trigger the following useEffect
    // which will load all BundleAttributions
    const reloadBundleAttributions = useCallback(() => {
        // Shouldn't use pagination parameters for endpoints that aren't paginated! Bundle Organization Attributions
        setBundleOrgAttributions([]);
        setBundleOrgAttrTotal(1000);
        setBundleOrgAttrPageSize(100);
        setBundleOrgAttrLoadingState('NOT STARTED');
        setBundleOrgAttrPage(0);
        setBundleOrgAttrLastPage(-1);
    }, []);

    useEffect(() => {
        // Load all pages of BundleAttributions
        if ((bundleOrgAttrLoadingState === 'NOT STARTED' || bundleOrgAttrLoadingState === 'LOADING') && bundleOrgAttrPage > bundleOrgAttrLastPage) {
            setBundleOrgAttrLastPage(bundleOrgAttrPage);
            setBundleOrgAttrLoadingState('LOADING');
            loadBundleOrganizationAttributionsPage();
        }
    }, [bundleOrgAttrLoadingState, bundleOrgAttrPage, bundleOrgAttrLastPage, loadBundleOrganizationAttributionsPage]);

    useEffect(() => {
        if (bundleOrgAttrLoadingState === 'SUCCESS' && attrLoadingState === 'NOT STARTED' && selectedType) {
            loadAllAttributionsPage({
                page: attrPage,
                pageSize: attrPageSize,
                type: selectedType
            });
        }
    }, [bundleOrgAttrLoadingState, attrLoadingState, attrPage, attrPageSize, selectedType, loadAllAttributionsPage]);

    const handleTypeChange = useCallback((newType: string) => {
        if (!newType || newType === selectedType) {
            return;
        }
        setSelectedType(newType);
        setAttrPage(0);
        setAttributions([]);
        setJoinedAttributions([]);
        setAttrLoadingState('NOT STARTED');
    }, [selectedType]);

    // Load Quote Experiences
    useEffect(() => {
        if (quoteExperiencesLoadingState === 'NOT STARTED') {
            setQuoteExperiencesLoadingState('LOADING');
            loadQuoteExperiences(paginationModelQuoteExperiences.page, paginationModelQuoteExperiences.pageSize);
        }
    }, [quoteExperiencesLoadingState, loadQuoteExperiences, paginationModelQuoteExperiences.page, paginationModelQuoteExperiences.pageSize]);


    const processCommand = useCallback(async (command: AttributionAssociationCommand) => {
        const organizationAttributionGuid = command.organizationAttributionGuid;

        const updateLoadingState = (state: AttributionLoadingState) => {
            setAttributionState(prev => ({
                ...prev,
                [organizationAttributionGuid]: {
                    ...prev[organizationAttributionGuid],
                    loadingState: state
                }
            }));
        };

        updateLoadingState('LOADING');
        setCommandProcessingState('LOADING');

        try {
            if (command.action === 'CREATE') {
                const createEndpoint = BundleService.addBundleAttribution;
                await createEndpoint({
                    bundleGuid: localBundleGuid!,
                    organizationAttributionGuid
                });
            } else if (command.action === 'DELETE') {
                const deleteEndpoint = BundleService.deleteBundleAttribution;
                await deleteEndpoint({
                    bundleGuid: localBundleGuid!,
                    organizationAttributionGuid
                });
            }

            updateLoadingState('NOT LOADING');
            reloadBundleAttributions();
        } catch (err) {
            console.error(err);
            updateLoadingState('NOT LOADING');
            setBundleOrgAttributionsError(`Failed to process command for ${organizationAttributionGuid}`);
        } finally {
            setCommandProcessingState('NOT LOADING');
        }
    }, [BundleService, localBundleGuid, reloadBundleAttributions]);

    const includedRadioCallback = useCallback((props: GridRenderCellParams<any, boolean>) => {
        const rowData = props?.row as JoinedOrganizationAttributionSummary | undefined;
        if (!rowData || !rowData.organizationAttributionGuid || !selectedType) {
            return;
        }
        const newGuid = rowData.organizationAttributionGuid;

        const deleteCommands: AttributionAssociationCommand[] = [];
        const nextState: Record<string, AttributionState> = { ...attributionState };

        Object.values(attributionState).forEach(entry => {
            if (
                entry.included &&
                entry.type === selectedType &&
                entry.organizationAttributionGuid &&
                entry.organizationAttributionGuid !== newGuid
            ) {
                deleteCommands.push({
                    organizationAttributionGuid: entry.organizationAttributionGuid,
                    action: 'DELETE',
                    included: false,
                });
                nextState[entry.organizationAttributionGuid] = {
                    ...entry,
                    included: false,
                    loadingState: 'LOADING',
                };
            }
        });

        nextState[newGuid] = {
            ...attributionState[newGuid],
            included: true,
            loadingState: 'LOADING',
        };

        setCommands([
            ...commands,
            ...deleteCommands,
            { organizationAttributionGuid: newGuid, action: 'CREATE', included: true }
        ]);
        setAttributionState(nextState);
    }, [commands, attributionState, selectedType]);

    const clearIncludedForType = useCallback(() => {
        if (!selectedType) {
            return;
        }
        const deleteCommands: AttributionAssociationCommand[] = [];
        const nextState: Record<string, AttributionState> = { ...attributionState };

        Object.values(attributionState).forEach(entry => {
            if (
                entry.included &&
                entry.type === selectedType &&
                entry.organizationAttributionGuid
            ) {
                deleteCommands.push({
                    organizationAttributionGuid: entry.organizationAttributionGuid,
                    action: 'DELETE',
                    included: false,
                });
                nextState[entry.organizationAttributionGuid] = {
                    ...entry,
                    included: false,
                    loadingState: 'LOADING',
                };
            }
        });

        if (!deleteCommands.length) {
            return;
        }

        setCommands([...commands, ...deleteCommands]);
        setAttributionState(nextState);
    }, [commands, attributionState, selectedType]);

    const handlePageChangeQuoteExperiences = useCallback((page: number, pageSize: number) => {
        setPaginationModelQuoteExperiences({ page, pageSize });
        loadQuoteExperiences(page, pageSize);
    }, [loadQuoteExperiences]);

    const displayedAttributions = useMemo(() => {
        if (!selectedType || !localBundleGuid) {
            return joinedAttributions;
        }
        const noneRow = {
            organizationAttributionGuid: NONE_ROW_GUID,
            attributionGuid: '',
            displayName: 'None',
            type: '',
            status: '',
            organizationGuid: organizationGuid!,
            included: false,
        } as unknown as JoinedOrganizationAttributionSummary;
        return [noneRow, ...joinedAttributions];
    }, [joinedAttributions, selectedType, localBundleGuid, organizationGuid]);

    const firstIncludedGuidForType = useMemo(() => {
        if (!selectedType) return null;
        const found = joinedAttributions.find(attr => attributionState[attr.organizationAttributionGuid!]?.included);
        return found?.organizationAttributionGuid ?? null;
    }, [joinedAttributions, attributionState, selectedType]);

    const renderIncludedRadio = useCallback((props: GridRenderCellParams<any, boolean>) => {
        const rowData = props?.row as JoinedOrganizationAttributionSummary | undefined;
        if (!rowData || !rowData.organizationAttributionGuid) {
            return null;
        }
        const isNoneRow = rowData.organizationAttributionGuid === NONE_ROW_GUID;
        const isChecked = isNoneRow
            ? firstIncludedGuidForType === null
            : rowData.organizationAttributionGuid === firstIncludedGuidForType;
        const isDisabled = (props?.colDef as unknown as Record<string, any> | undefined)?.disabled;
        const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
            if (!event.target.checked) {
                return;
            }
            if (isNoneRow) {
                clearIncludedForType();
            } else {
                includedRadioCallback(props);
            }
        };
        return <Radio
            disabled={isDisabled}
            checked={isChecked}
            onChange={handleChange}
        />;
    }, [firstIncludedGuidForType, includedRadioCallback, clearIncludedForType]);

    const renderQuoteExperienceRadio = useCallback(
        (props: GridRenderCellParams<any, boolean>) => {
            const rowData = props?.row as Bundle | undefined;
            if (!rowData) return null;

            const isChecked = selectedQuoteExperienceGuid === rowData.bundleGuid;

            const handleClick = () => {
                setSelectedQuoteExperienceGuid(isChecked ? null : rowData.bundleGuid!);
                setQuoteExperienceChanged(true);
            };

            return (
                <Radio
                    checked={isChecked}
                    onClick={handleClick}
                />
            );
        },
        [selectedQuoteExperienceGuid]
    );

    const quoteExperienceColumns = useMemo(() => {
        return [
            {
                field: 'include',
                headerName: 'Include',
                width: 150,
                sortable: false,
                renderCell: renderQuoteExperienceRadio,
            },
            {
                field: 'displayName',
                headerName: 'Name',
                width: 300,
                editable: false,
                renderCell: (cellValues) => (
                    <span>{cellValues.row.displayName || 'No Quote Experiences'}</span>
                ),
            },
        ] as GridColDef<JoinedQuoteExperienceSummary>[];
    }, [renderQuoteExperienceRadio]);

    const columns = useMemo(() => {
        return [
            {
                field: 'included',
                headerName: 'Selected',
                width: 100,
                disabled: !localBundleGuid,
                editable: false,
                renderCell: renderIncludedRadio,
            },
            {
                field: 'displayName',
                headerName: 'Name',
                width: 300,
                editable: false,
            },
            {
                field: 'type',
                headerName: 'Type',
                width: 150,
                editable: false,
            },
            {
                field: 'status',
                headerName: 'Status',
                width: 100,
                editable: false,
            },
            {
                field: 'editLink',
                headerName: 'Edit',
                width: 100,
                editable: false,
                renderCell: (cellValues) => {
                    if (cellValues.row.organizationAttributionGuid === NONE_ROW_GUID) {
                        return null;
                    }
                    return <Link to={`/attribution/${cellValues.row.attributionGuid}`}>Edit</Link>;
                }
            },
        ] as GridColDef<JoinedOrganizationAttributionSummary>[];
    }, [localBundleGuid, renderIncludedRadio]);

    const appliedAttributionColumns = useMemo(() => {
        return [
            {
                field: 'displayName',
                headerName: 'Name',
                width: 300,
                editable: false,
            },
            {
                field: 'type',
                headerName: 'Type',
                width: 150,
                editable: false,
            },
            {
                field: 'status',
                headerName: 'Status',
                width: 100,
                editable: false,
            },
            {
                field: 'editLink',
                headerName: 'Edit',
                width: 100,
                editable: false,
                renderCell: (cellValues) => (
                    <Link to={`/attribution/${cellValues.row.attributionGuid}`}>Edit</Link>
                ),
            },
        ] as GridColDef<BundleAttribution>[];
    }, []);

    // PROCESS STATE UPDATE COMMANDS
    useEffect(() => {
        if (!commands.length || commandProcessingState === 'LOADING') {
            return
        }
        const commandsCopy = [...commands];
        const nextCommand = commandsCopy.shift();
        setCommands(commandsCopy);
        setCommandProcessingState('LOADING');
        setAttributionState({
            ...attributionState,
            [nextCommand!.organizationAttributionGuid]: {
                ...attributionState[nextCommand!.organizationAttributionGuid],
                loadingState: 'LOADING'
            }
        });
        processCommand(nextCommand!);
    }, [commands, commandProcessingState, attributionState, processCommand]);

    const handlePageChange = useCallback((page: number, pageSize: number) => {
        setAttrPage(page);
        setAttrPageSize(pageSize);
        loadAllAttributionsPage({ page, pageSize, type: selectedType });
    }, [setAttrPage, setAttrPageSize, loadAllAttributionsPage, selectedType]);

    const isSuccess = (resp: any) => {
        if (!resp) return false;
        if (typeof resp.ok === 'boolean') return resp.ok;
        const status = Number(resp.status);
        return status >= 200 && status < 300;
    };


    const saveBundle = useCallback(async (saveAndContinue: boolean) => {
        setBundleError('');
        setIsSaving(true);
        let childBundleGuid = localBundleGuid;
        let navPath: string = saveAndContinue && localBundleGuid
            ? `/organization/${organizationGuid}/bundles/${localBundleGuid}`
            : `/organization/${organizationGuid}`;
        let errorEncountered: boolean = false;

        if (!localBundleGuid) {
            const createBundle = BundleService.createBundle;
            const createBundlePayload: Record<string, any> = {
                displayName,
                organizationGuid
            };
            if (quoteExperienceChanged) {
                createBundlePayload['parentGuid'] = selectedQuoteExperienceGuid;
            }
            await createBundle(createBundlePayload)
                .then((response: ApiServiceEndpointResponse<string>) => {
                    setLocalBundleGuid(response.textBody!);
                    childBundleGuid = response.textBody!;
                    if (saveAndContinue) {
                        navPath = `/organization/${organizationGuid}/bundles/${childBundleGuid}`;
                    }
                })
                .catch(e => {
                    if (e?.status === 409) {
                        setBundleError("A Bundle with this name already exists.")
                    } else {
                        setBundleError('Failed to save Bundle');
                    }
                    errorEncountered = true;
                })
                .finally(() => {
                    setIsSaving(false)
                })
        } else if (quoteExperienceChanged) {
            const updateBundle = BundleService.updateBundle;
            await updateBundle({
                bundleGuid: childBundleGuid,
                parentGuid: selectedQuoteExperienceGuid
            })
                .then((resp: any) => {
                    if (!isSuccess(resp.response)) {
                        setBundleError("Failed to link Bundle to Quote Experience")
                    }
                })
                .catch(e => {
                    setBundleError('Failed to link Bundle to Quote Experience');
                    errorEncountered = true;
                })
                .finally(() => setIsSaving(false));
        }

        if (!errorEncountered) {
            navigate(navPath);
        }
    }, [BundleService, displayName, organizationGuid, selectedQuoteExperienceGuid, localBundleGuid, navigate, quoteExperienceChanged]);

    const isPageLoading = useMemo(() => {
        const isSettled = (s: LoadingState) => s === 'SUCCESS' || s === 'FAIL';

        const bundleBusy = !!localBundleGuid && !isSettled(bundleLoadingState);
        const bundleOrgAttrBusy = !!localBundleGuid && !isSettled(bundleOrgAttrLoadingState);
        const quoteExpBusy = !!organizationGuid && !isSettled(quoteExperiencesLoadingState);
        const attrBusy = !!selectedType && bundleOrgAttrLoadingState === 'SUCCESS' && !isSettled(attrLoadingState);

        return (
            bundleBusy ||
            bundleOrgAttrBusy ||
            quoteExpBusy ||
            attrBusy ||
            commandProcessingState === 'LOADING' ||
            isSaving
        );
    }, [
        localBundleGuid,
        organizationGuid,
        bundleLoadingState,
        bundleOrgAttrLoadingState,
        quoteExperiencesLoadingState,
        attrLoadingState,
        selectedType,
        commandProcessingState,
        isSaving,
    ]);

    function attributionForm() {
        return (
            <>
                <AppFormRow>
                    <AppFormCell>
                        <FormLabel htmlFor="name" >Name</FormLabel>
                        <FormGroup row>
                            <AppTextField
                                name="name"
                                data-testid="name"
                                inputProps={{ "data-testid": "bundle-name" }}
                                value={displayName}
                                disabled={!!localBundleGuid}
                                required={!localBundleGuid}
                                error={!!bundleError}
                                helperText={bundleError}
                                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                                    setDisplayName(event.target.value)
                                }
                            />
                        </FormGroup>
                    </AppFormCell>
                </AppFormRow>
                <AppFormRow>
                    <CollapsibleSection title="Quote Experience">
                        <AppTableWithButtons
                            rowIdField='bundleGuid'
                            pagination
                            totalRecords={totalQuoteExperienceRecords}
                            rows={quoteExperiences}
                            isLoading={quoteExperiencesLoadingState === 'LOADING'}
                            columns={quoteExperienceColumns}
                            onPageChange={handlePageChangeQuoteExperiences}
                        />
                    </CollapsibleSection>
                </AppFormRow>
                <AppFormRow>
                    <CollapsibleSection title="Attributions in this Bundle">
                        <AppTableWithButtons
                            className='bundle-attributions-table'
                            rowIdField='organizationAttributionGuid'
                            rows={bundleOrgAttributions}
                            columns={appliedAttributionColumns}
                            isLoading={bundleOrgAttrLoadingState === 'LOADING'}
                            slots={{
                                noRowsOverlay: () => (
                                    <div className="bundle-attributions-empty">
                                        <Text>No attributions are currently applied to this bundle.</Text>
                                    </div>
                                ),
                            }}
                        />
                    </CollapsibleSection>
                </AppFormRow>
                <AppFormRow>
                    <AppTableWithButtons
                        className='bundle-attributions-table'
                        rows={displayedAttributions}
                        rowIdField='organizationAttributionGuid'
                        columns={columns}
                        title='Attributions available within this Organization'
                        pagination={attrTotal > attrPageSize ? true : undefined}
                        totalRecords={attrTotal}
                        isLoading={attrLoadingState === 'LOADING'}
                        onPageChange={handlePageChange}
                        page={attrPage}
                        headerExtra={
                            <>
                                <FormLabel htmlFor="attribution-type" required>
                                    Attribution Type
                                </FormLabel>
                                <AppSelect
                                    id="attribution-type"
                                    name="attribution-type"
                                    value={selectedType ?? ''}
                                    displayEmpty
                                    size="small"
                                    onChange={(event: SelectChangeEvent<unknown>) =>
                                        handleTypeChange(event.target.value as string)
                                    }
                                >
                                    <MenuItem value="" disabled>Select an attribution type</MenuItem>
                                    {latestSchemas.map(schema => (
                                        <MenuItem key={schema.schema_type} value={schema.schema_type}>
                                            {schema.schema_type}
                                        </MenuItem>
                                    ))}
                                </AppSelect>
                            </>
                        }
                        slots={{
                            noRowsOverlay: () => (
                                <div className="bundle-attributions-empty">
                                    {!selectedType
                                        ? <Text>Select an attribution type to view available attributions.</Text>
                                        : <Text>No {selectedType} attributions are associated with this organization.</Text>
                                    }
                                </div>
                            ),
                        }}
                    />
                </AppFormRow>
                {
                    !localBundleGuid
                        ? <Text>Save this bundle to begin adding Attributions</Text>
                        : null
                }
            </>
        )
    }

    function getFormDisplay() {
        if (attrLoadingState === 'FAIL' || bundleOrgAttrLoadingState === 'FAIL') {
            return (
                <>
                    {bundleError ? <Text>{bundleError}</Text> : null}
                    {bundleOrgAttributionsError ? <Text>{bundleOrgAttributionsError}</Text> : null}
                    {quoteExperiencesError ? <Text>{quoteExperiencesError}</Text> : null}
                    {attrError ? <Text>{attrError}</Text> : null}
                    {schemasError ? <Text>{attrError}</Text> : null}
                </>
            );
        }


        if (bundleOrgAttrLoadingState === 'SUCCESS' || bundleFormReady) {
            return attributionForm();
        }

        return null;
    }


    return (
        <AppPage
            title={localBundleGuid ? 'Add Bundle Attributions' : 'Create Bundle'}
            isLoading={isPageLoading}
            buttonRowContent={(
                <AppFormCell>
                    {
                        !localBundleGuid ? (
                            <Button
                                variant="outlined"
                                style={{ marginRight: "1rem" }}
                                onClick={() => saveBundle(true)}
                                startIcon={isSaving ? 'Saving' : null}
                            >
                                {isSaving ? 'Saving...' : 'Save and Continue'}
                            </Button>
                        ) : quoteExperienceChanged && (
                            <Button
                                variant="outlined"
                                style={{ marginRight: "1rem" }}
                                onClick={() => saveBundle(false)}
                                disabled={isSaving}
                                startIcon={isSaving ? 'Saving' : null}
                            >
                                {isSaving ? 'Saving...' : 'Save'}
                            </Button>

                        )
                    }
                    <Button variant="outlined" onClick={() => navigate(`/organization/${organizationGuid}`)} >Close</Button>
                </AppFormCell>
            )}
        >
            {getFormDisplay()}
        </AppPage>
    )
}